import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import ProfileField from './profile/ProfileField';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import {
  checkIfHasErrors,
  getErrors,
  getValidationRules,
} from '../../helpers/helper';
import { PROFILE_BLOCK } from '../../../../services/utilityFunctions';
import ErrorMessage from '../../../../commonComponents/forms/ErrorMessage';

function stateFromProps(member = {}) {
  const softSkills = member.softSkills || {};
  return {
    positiveSkills: softSkills.positiveSkills || [],
    negativeSkills: softSkills.negativeSkills || [],
    qualities: softSkills.qualities || '',
    backwards: softSkills.backwards || '',
  };
}

/**
 * Form to update the current logged user
 */
function SoftSkillsForm({ t, member, onMemberChange, onSkipStep }) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const [state, setState] = useState(stateFromProps(member));
  const [errors, setErrors] = useState({
    positiveSkills: false,
    negativeSkills: false,
    qualities: false,
    backwards: false,
  });
  useEffect(() => setState(stateFromProps(member)), [member]);

  const formId = 'softSkillsForm';
  const validationRules = useMemo(
    () =>
      getValidationRules(requiredFieldsPerBlock, PROFILE_BLOCK.SOFT_SKILLS, {}),
    [getValidationRules, requiredFieldsPerBlock],
  );

  const handleSubmit = async () => {
    const newErrors = getErrors(validationRules, state);

    if (checkIfHasErrors(newErrors)) {
      setErrors(newErrors);
      return;
    }

    const params = {
      positiveSkills: state.positiveSkills,
      negativeSkills: state.negativeSkills,
      qualities: state.qualities,
      backwards: state.backwards,
    };
    onMemberChange(params, 'softSkills');
  };

  const handleChange = useCallback((field, value) => {
    setErrors(prevErrors => ({ ...prevErrors, [field]: false }));
    setState(prevState => ({ ...prevState, [field]: value }));
  }, []);

  const inputProps = useCallback(() => undefined, []);

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle title={t('profile_tab_softSkills')} />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrapper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            <ProfileField
              field="positiveSkills"
              type="tags"
              value={state.positiveSkills}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOFT_SKILLS}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
              labelClassName={`col-lg-2 col-sm-12 ${
                errors?.positiveSkills ? 'label-error' : ''
              }`}
            />
            {errors?.positiveSkills && <ErrorMessage />}
            <ProfileField
              field="negativeSkills"
              type="tags"
              value={state.negativeSkills}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOFT_SKILLS}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
              labelClassName={`col-lg-2 col-sm-12 ${
                errors?.negativeSkills ? 'label-error' : ''
              }`}
            />
            {errors?.negativeSkills && <ErrorMessage />}
            <ProfileField
              field="qualities"
              type="textarea"
              value={state.qualities}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOFT_SKILLS}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
              labelClassName={`col-lg-2 col-sm-12 ${
                errors?.qualities ? 'label-error' : ''
              }`}
            />
            {errors?.qualities && <ErrorMessage />}
            <ProfileField
              field="backwards"
              type="textarea"
              value={state.backwards}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.SOFT_SKILLS}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
              labelClassName={`col-lg-2 col-sm-12 ${
                errors?.backwards ? 'label-error' : ''
              }`}
            />
            {errors?.backwards && <ErrorMessage />}
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

SoftSkillsForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func,
};

SoftSkillsForm.defaultProps = {
  onSkipStep: null,
};

export default translate(SoftSkillsForm);
